import React, { useEffect, useRef, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { FormattedMessage } from 'react-intl';
import { DATE_RANGE_SELECTED, getDateRangeRadioOptions } from '../ReportsUtils';
import PropTypes from 'prop-types';
import { CircularProgress, makeStyles } from '@material-ui/core';
import { lighten } from '@material-ui/core/styles';
import {
  BACKGROUND_WHITE,
  BLACK_COLOR, DARK_BORDER_COLOR,
  LIGHT_GREY,
  LIGHT_GREY_COLOR,
} from '../../../../const/color-const';
import { STANDARD_INPUT_HEIGHT, STANDARD_INPUT_RADIUS, STANDARD_INPUT_WIDTH } from '../../../../const/chart-const';
import { PERIODIC_WITHOUT_FUTURE_DATES, YESTERDAY } from '../../../../utils/dates-const';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { downloadPeriodicReportsByDate } from '../../../../actions/report-actions';
import CommonButton from '../../../common/CommonButton';
import LoadingSnackbar from '../../../common/LoadingSnackbar';
import { useValidator } from '../../../../utils/validators';
import CommonSelectField from '../../../common/form/CommonSelectField';
import CommonMultiSelectField from '../../../common/form/CommonMultiSelectField';
import { generateUniqueCustomerOptions, transformToMachineOptions } from '../../../../utils/report-utils';

const CUSTOM_INPUT_WIDTH = 280;

const styles = makeStyles(theme => ({
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  formContent: {
    width: '100%',
    display: 'flex',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    '& .commonSelectField': {
      width: STANDARD_INPUT_WIDTH,
      backgroundColor: theme.palette.type === 'light' ? lighten(BACKGROUND_WHITE, 0.7) : BLACK_COLOR,
      margin: theme.spacing(1, 1, 1, 1),
    },
    '& > div:nth-child(2)': {
      '& .commonSelectField': {
        marginRight: theme.spacing(3),
      },
    },
  },
  topBar: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    margin: theme.spacing(1),
    '& .commonSelectField, & .MultiSelectField': {
      width: CUSTOM_INPUT_WIDTH,
      margin: theme.spacing(1, 1, 1, 1),
      [theme.breakpoints.down('sm')]: {
        width: STANDARD_INPUT_WIDTH,
      },
    },
  },
  datePickersWrapper: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    '& > div': {
      backgroundColor: theme.palette.type === 'light' ? lighten(BACKGROUND_WHITE, 0.7) : BLACK_COLOR,
      borderRadius: STANDARD_INPUT_RADIUS,
      height: STANDARD_INPUT_HEIGHT,
      width: STANDARD_INPUT_WIDTH,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: theme.spacing(1, 3, 1, 1),
      border: theme.palette.type === 'light' ? `1px solid ${LIGHT_GREY_COLOR}` : `1px solid ${DARK_BORDER_COLOR}`,
      '&:hover': {
        border: theme.palette.type === 'light' ? `1px solid ${BLACK_COLOR}` : `1px solid white`,
      },
      '& label': {
        border: 'none',
        marginLeft: 10,
        '&.Mui-error': {
          top: 6,
        },
      },
      '& p': {
        position: 'relative',
        marginLeft: 10,
        '&.Mui-error': {
          top: -6,
        },
      },
      '& input': {
        marginLeft: 10,
        border: 'none',
        height: '100%',
      },
      '& div': {
        '&::before': {
          display: 'none',
        },
        '&::after': {
          display: 'none',
        },
      },
    },
  },
  button: {
    width: 300,
    backgroundColor: LIGHT_GREY,
  },
  sectionWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(5),
    marginTop: theme.spacing(2),
    maxWidth: 1600,
    width: '100%',
    '& button': {
      width: '100%',
      maxWidth: 240,
    },
  },
  description: {
    marginLeft: theme.spacing(5),
  },
  loadingBar: {
    display: 'flex',
    alignSelf: 'center',
    marginTop: theme.spacing(2),
  },
}));

const validator = ({ values, intl: { formatMessage } }) => {
  const { dateFrom, dateTo } = values;
  const yesterdayDate = new Date(YESTERDAY.option.dateFrom);
  const errors = {};
  
  if (yesterdayDate < dateFrom) {
    errors.dateFrom = formatMessage({
      id: 'date.from.cannot.exceed',
      defaultMessage: 'Date from cannot exceed yesterday',
    });
  }
  if (yesterdayDate < dateTo) {
    errors.dateTo = formatMessage({
      id: 'date.to.cannot.exceed',
      defaultMessage: 'Date to cannot exceed yesterday',
    });
  }
  return errors;
};

const PeriodicReportForm = ({ machines, downloadPeriodicReportsByDate }) => {
  const [dateRangeOption, setDateRangeOption] = useState(DATE_RANGE_SELECTED);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const machinesOptions = transformToMachineOptions(machines);
  const uniqueCustomers = generateUniqueCustomerOptions(machines);
  const INIT_CUSTOMER_VALUE = uniqueCustomers[0].value;
  const filterCustomers = (companyName) => (companyName ? machinesOptions.filter(machine => machine.customerName === companyName) : []);
  const [customer, setCustomer] = useState(machinesOptions);
  const formValidator = useValidator(validator);
  const formikRef = useRef();
  const classes = styles();
  
  const updateMachineIDs = (machineData) => {
    formikRef.current.setFieldValue('machineIDs', [machineData[0]?.value]);
    setCustomer(machineData);
  };
  
  useEffect(() => {
    if (formikRef.current) {
      updateMachineIDs(filterCustomers(INIT_CUSTOMER_VALUE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize={true}
      initialValues={{
        customerNames: INIT_CUSTOMER_VALUE,
        dateTo: YESTERDAY.option.dateTo,
        dateFrom: YESTERDAY.option.dateTo,
        dateRange: YESTERDAY.value,
      }}
      onSubmit={(values, formikHelpers) => {
        downloadPeriodicReportsByDate(values, machines, formikHelpers, setDownloadProgress);
      }}
      validate={formValidator}>
      {({ isSubmitting, values, setFieldValue, isValid }) => {
  
        return (
          <Form className={classes.formWrapper}>
            <div className={classes.formContent}>
              <div className={classes.topBar}>
                <Field
                  wide
                  atLeastOneElement
                  name="customerNames"
                  label={<FormattedMessage
                    id="field.company.names"
                    defaultMessage="Customer"
                  />}
                  options={uniqueCustomers}
                  component={CommonSelectField}
                  extraHandleChangeAction={(values) => {
                    const data = filterCustomers(values);
                    updateMachineIDs(data);
                  }}
                />
  
                <Field
                  wide
                  atLeastOneElement
                  name="machineIDs"
                  label={<FormattedMessage
                    id="field.cash.register"
                    defaultMessage="Cash Register"
                  />}
                  options={customer}
                  component={CommonMultiSelectField}
                />
              </div>
              {getDateRangeRadioOptions(setDateRangeOption, values, setFieldValue, dateRangeOption, classes, PERIODIC_WITHOUT_FUTURE_DATES)}
            </div>
  
            {isSubmitting && <CircularProgress className={classes.loadingBar} />}
  
            <LoadingSnackbar
              downloadFile={true}
              progress={downloadProgress}
              label={<FormattedMessage id="loading" defaultMessage="Loading" />}
            />
  
            <div className={classes.sectionWrapper}>
              <CommonButton
                type="submit"
                isAnimated
                size="large"
                disabled={!!isSubmitting || !values.dateFrom || !values.dateTo || !isValid}
                label={<FormattedMessage id="page.periodicReport.PDFreport.button"
                                         defaultMessage="Download periodic report" />}
              />
              <div className={classes.description}>
                <FormattedMessage
                  id="page.periodicReport.PDFreport.description"
                  defaultMessage="Open report for selected period. The report is displayed as a pdf file in A4 format."
                />
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

PeriodicReportForm.propTypes = {
  machines: PropTypes.array,
  downloadPeriodicReportsByDate: PropTypes.func,
};

const decorate = compose(
  connect(
    null,
    dispatch => bindActionCreators({ downloadPeriodicReportsByDate }, dispatch)),
);

export default decorate(PeriodicReportForm);



